import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { DragSource } from 'react-dnd';
import { bindActionCreators } from 'redux';
import * as accountActions from '../../../actions/accountActions';
import * as drawerActions from '../../../actions/drawerActions';
import * as classroomActions from '../../../actions/classroom/classroomActions';
import * as classroomGroupActions from '../../../actions/classroom/classroomGroupActions';
import { GROUPS, ROLE, DRAWER_CONTENTS } from '../../../const';
import * as messageAlert from '../../../actions/messageAlertAction';
import * as groupsApi from '../../../services/api/groupApi';
import * as revealActions from '../../../actions/revealActions';
import * as sectionActions from '../../../actions/sectionActions';
import { DropdownOptions, Paragraph } from '@netexknowledge/smart-components';
import {
  CardContent,
  CardName,
  GreenBall,
  GreyBall,
  GroupLetter,
  Image,
  MoreVert,
  StudentGroups,
  TeacherRole
} from './ParticipantsComponents';

const boxSource = {
  beginDrag(props) {
    return {
      student: props.student
    };
  }
};

export class ParticipantCard extends React.Component {
  renderGroupLetter = groups => {
    const { colors } = this.props;

    if (groups) {
      return (
        <StudentGroups>
          {groups.map((g, index) => {
            const groupStyle = {
              color: colors[`group0${g.colour}`],
              borderColor: colors[`group0${g.colour}`]
            };
            return (
              <GroupLetter letterStyle={groupStyle} key={index}>
                {groupsApi.getGroupFirstChar(g)}
              </GroupLetter>
            );
          })}
        </StudentGroups>
      );
    }
  };

  handlerClose = () => {
    this.setState({
      open: false
    });
  };

  renderConnected = connected => {
    return connected ? <GreenBall /> : <GreyBall />;
  };

  goToStudentProfile = () => {
    const { isAppOnline, student, drawerActions, classroomActions } = this.props;
    if (isAppOnline) {
      classroomActions.getSelectedStudentProfile(student);
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.VIEW_STUDENT_PROFILE_PANEL);
    }
  };

  removeStudentFromGroup = () => {
    const { isAppOnline } = this.props;
    if (isAppOnline) {
      const { classroomGroupActions, group, classroom, student } = this.props;
      classroomGroupActions.removeStudentFromGroup(student._id, group.id, classroom);
    }
  };

  openUnenrollModal = () => {
    const { classroomActions, student } = this.props;
    classroomActions.setSelectedStudentProfile(student);
    classroomActions.setUnenrollModalState(true);
  };

  openEditRoleModal = () => {
    const { classroomActions, student } = this.props;
    classroomActions.setSelectedStudentProfile(student);
    classroomActions.setEditRoleModalState(true);
  };

  getOpositeRoleTranslation = role =>
    role === ROLE.INSTRUCTOR ? I18n.t('participants.roles.coinstructor') : I18n.t('participants.roles.instructor');

  getRoleTranslation = role =>
    role === ROLE.INSTRUCTOR ? I18n.t('participants.roles.instructor') : I18n.t('participants.roles.coinstructor');

  getOptionItems = (group, _id, role) => {
    const options = [];
    const { configuration, participantId } = this.props;

    if (this.isTeacherTab()) {
      if (_id !== participantId) {
        if (configuration.enableEnrollment) {
          options.push({
            key: 1,
            text: I18n.t('participants.modal.teachers.delete'),
            onClick: this.openUnenrollModal,
            icon: 'custom-icons-waste-can',
            disabled: false
          });
          options.push({
            key: 2,
            text: `${I18n.t('participants.modal.teachers.editRole')} ${this.getOpositeRoleTranslation(
              role
            ).toLowerCase()}`,
            onClick: this.openEditRoleModal,
            icon: 'custom-icons-options-gear',
            disabled: false
          });
        }
      }
    } else {
      if (!configuration.hideUserConfig) {
        options.push({
          key: 1,
          text: I18n.t('studentList.viewProfile'),
          onClick: this.goToStudentProfile,
          icon: 'custom-icons-eye-view',
          disabled: false
        });
      }

      if (GROUPS.EVERYONE.id === group.id) {
        if (configuration.unenrollStudents) {
          options.push({
            key: 2,
            text: I18n.t('studentList.removeStudent'),
            onClick: this.openUnenrollModal,
            icon: 'custom-icons-waste-can',
            disabled: false
          });
        }
      } else {
        options.push({
          key: 3,
          text: I18n.t('studentList.removeStudentGroup'),
          onClick: this.removeStudentFromGroup,
          icon: 'custom-icons-waste-can',
          disabled: false
        });
      }
    }

    return options;
  };

  isTeacherTab = () => this.props.isTeacherView;

  isTeacher = () => this.props.role === ROLE.INSTRUCTOR;

  render() {
    const { connectDragSource, student, group, participantId } = this.props;
    const { surname, name, groups, _id, connected, role, picture, enabled } = student;

    accountActions.updateUserData();

    const optionItems =
      ((group || this.isTeacherTab()) && this.isTeacher() && this.getOptionItems(group, _id, role)) || [];

    const card = (
      <div className="row student-card">
        {this.renderConnected(connected)}
        <Image ref={`studentImg${_id}`} src={picture} alt="presentation" isEnabled={enabled} />
        <CardContent className="column drag" isEnabled={enabled}>
          <Paragraph className="text-grey-700 text--1line tooltip top" dataText={`${name} ${surname}`}>
            <CardName>
              {name}
            </CardName>
          </Paragraph>
          {this.isTeacherTab() && <TeacherRole>{this.getRoleTranslation(role).toUpperCase()}</TeacherRole>}
          {groups && this.renderGroupLetter(groups)}
        </CardContent>
        <div className="margin-top-4 margin-right-4">
          {this.isTeacher() && _id !== participantId && optionItems.length > 0 && (
            <DropdownOptions id={_id} color="gray" dropdownSide="left" options={optionItems} />
          )}
        </div>
      </div>
    );

    return this.isTeacher() ? connectDragSource(card) : card;
  }
}

ParticipantCard.propTypes = {
  role: PropTypes.string,
  student: PropTypes.object,
  updatedUser: PropTypes.object,
  isDragging: PropTypes.bool.isRequired,
  group: PropTypes.any,
  connectDragSource: PropTypes.any,
  colors: PropTypes.any,
  revealActions: PropTypes.object,
  classroomGroupActions: PropTypes.object,
  classroom: PropTypes.any,
  isAppOnline: PropTypes.any,
  accountActions: PropTypes.object,
  sectionActions: PropTypes.object,
  classroomActions: PropTypes.object,
  configuration: PropTypes.object,
  participantId: PropTypes.string,
  activeClassroomTab: PropTypes.string
};

function mapStateToProps(state) {
  return {
    classroom: state.classrooms.selectedClassroom.classroom,
    i18n: state.i18n,
    isAppOnline: state.connection.status.online,
    colors: state.theme.colors,
    updatedUser: state.account.updatedUser,
    configuration: state.account.configuration,
    activeClassroomTab: state.classrooms.selectedClassroom.activeClassroomTab,
    participantId: state.account.user._id,
    role: state.classrooms.selectedClassroom.classroom.role
  };
}

function mapDispatchToProps(dispatch) {
  return {
    accountActions: bindActionCreators(accountActions, dispatch),
    sectionActions: bindActionCreators(sectionActions, dispatch),
    classroomActions: bindActionCreators(classroomActions, dispatch),
    classroomGroupActions: bindActionCreators(classroomGroupActions, dispatch),
    messageAlert: bindActionCreators(messageAlert, dispatch),
    revealActions: bindActionCreators(revealActions, dispatch),
    drawerActions: bindActionCreators(drawerActions, dispatch),
    dispatch
  };
}

function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging()
  };
}

const DragSourceParticipantCard = DragSource('studentCard', boxSource, collect)(ParticipantCard);

export default connect(mapStateToProps, mapDispatchToProps)(DragSourceParticipantCard);
